import React, { useEffect, useState } from 'react';
import { Container, Form, Row, Col, Button, Card } from 'react-bootstrap';
import AlertOverlay from '../../../components/Alert';
import {
  getUiInterface,
  getPageName,
  getWordConnector,
} from '../../../locales/translationUtils';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FaChevronDown } from 'react-icons/fa';
import NavBar, { NavBarItem } from '../../../components/NavBar';
import { useGeneric } from '../../../apis/generic/GenericProvider';
import { CargoResponse, Dimensions } from '../../../services/cargoService';
import { getCargoById } from '../../../services/cargoService';

type ParamType = {
  id: string;
};

const CargoEditView: React.FC = () => {
  const { t } = useTranslation();
  const { distinctOptions, fetchDistinctOptions } = useGeneric();
  const location = useLocation();
  const { id } = useParams<ParamType>();
  const cargo: CargoResponse = location.state || {}; 
  const dimensions: Dimensions[] = cargo.dimensions || [];
  const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());

  const [alert, setAlert] = useState<{
    message: string;
    variant: 'success' | 'danger' | 'warning' | 'info';
  } | null>(null);
  
  const [formData, setFormData] = useState({
    FromId: cargo.from?.id || '',
    ClientId: cargo.client?.id || '',
    SupplierId: cargo.supplier?.id || '',
    Pu: cargo.pu || '',
    Dawb: cargo.dawb || '',
    ArrivalDate: cargo.arrivalDate || '',
    Dimensions: dimensions || [],
    Dgr: cargo.dgr || false,
    DgrGoods: cargo.dgrGoods || false,
    SoNumber: cargo.soNumber || '',
    WrNumber: cargo.wrNumber || '',
    CargoContents: cargo.cargoContents || [],
    Description: cargo.description || '',
    PoNumber: cargo.poNumber || '',
    Weight: (cargo as any).weightRaw ?? cargo.weight ?? 0,  
    Pieces: cargo.pieces || 0,
    Invoice: cargo.carrierInformations?.length > 0 ? cargo.carrierInformations[0].invoice : '',
    DocumentTypeId: cargo.carrierInformations?.length > 0 ? cargo.carrierInformations[0].document.id : ''
  });

  const initialFormData = location.state?.formData
  interface Dimension {
    Quantity: number;
    Length: number;
    Width: number;
    High: number;
  }

  interface CargoFormData {
    FromId: string;
    ClientId: string;
    SupplierId: string;
    Pu: string;
    Dawb: string;
    ArrivalDate: string;
    Pieces: number;
    Dimensions: Dimension[];
    Dgr: boolean;
    DgrGoods: boolean;
    Weight: number;
    PoNumber: string;
    SoNumber: string;
    WrNumber: string;
    Description: string;
  }

  useEffect(() => {
    if (id) {
      getCargoById(Number(id)).then((response) => {
        const data = response.data;
        setFormData( initialFormData ? initialFormData :{
          FromId: data.from?.id || '',
          ClientId: data.client?.id || '',
          SupplierId: data.supplier?.id || '',
          Pu: data.pu || '',
          Dawb: data.dawb || '',
          ArrivalDate: data.arrivalDate || '',
          Pieces: data.pieces || 0,
          Dimensions: data.dimensions || [],
          Dgr: data.dgr || false,
          DgrGoods: data.dgrGoods || false,
          Weight: data.weightRaw ?? data.weight ?? 0,
          PoNumber: data.poNumber || '',
          SoNumber: data.soNumber || '',
          WrNumber: data.wrNumber || '',
          Description: data.description || '',
          CargoContents: data.cargoContents || [],
          Invoice: data.carrierInformations?.length > 0 ? data.carrierInformations[0].invoice : '',
          DocumentTypeId: data.carrierInformations?.length > 0 ? data.carrierInformations[0].document.id : ''
        });
      });
    }
  }, [id]);

  const navItems: NavBarItem[] = [
    { label: t('cargo.labels.home'), route: '/', iconName: 'house-door' },
    { label: t('cargo.labels.cargoList'), route: '/cargo-info', iconName: 'table' },
    { label: t('cargo.labels.supplierList'), route: '/cargo-info/', iconName: 'info-circle' },
  ];

  useEffect(() => {
    fetchDistinctOptions('wareHouse');
    fetchDistinctOptions('client');
    fetchDistinctOptions('supplier');
  }, []);

  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;

    const numericValue = Number(value);
    if (numericValue < 0) {
      return;
    }

    setFormData((prevState) => ({
      ...prevState,
      [name.charAt(0).toUpperCase() + name.slice(1)]:
        type === 'checkbox' ? checked : value,
    }));    
  };

  const handleDimensionChange = (index: number, e: any) => {
    const { name, value } = e.target;

    const numericValue = Number(value);
    if (numericValue < 0) {
      return;
    }
    const updatedDimensions = [...formData.Dimensions];
    updatedDimensions[index] = {
      ...updatedDimensions[index],
      [name]: Number(value),
    };

    setFormData((prevState) => ({
      ...prevState,
      Dimensions: updatedDimensions,
    }));
  };

  const addDimension = () => {
    setFormData((prevState) => ({
      ...prevState,
      Dimensions: [
        ...prevState.Dimensions,
        { quantity: 0, id: 0, length: 0, width: 0, height: 0 },
      ],
    }));
  };

  const removeDimension = (index: number) => {
    const updatedDimensions = formData.Dimensions.filter((_, i) => i !== index);
    setFormData((prevState) => ({
      ...prevState,
      Dimensions: updatedDimensions,
    }));
  };

  const handleNextPage = () => {
    //TO DO: Implementar redirecionamento para a próxima página.
    navigate(`step-two`, { state: { formData } });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const nullRequiredFields: string[] = [];

    if (!formData.FromId) nullRequiredFields.push('From');
    if (!formData.ClientId) nullRequiredFields.push('Client');
    if (!formData.SupplierId) nullRequiredFields.push('Fornecedor');
    if (!formData.Weight || Number(formData.Weight) === 0) nullRequiredFields.push('WGT');
    if (!formData.PoNumber || Number(formData.PoNumber) == 0) nullRequiredFields.push('poNumber');
    if (!formData.WrNumber || Number(formData.WrNumber) == 0) nullRequiredFields.push('wrNumber');

    if (nullRequiredFields.length > 0) {
         setInvalidFields(new Set(nullRequiredFields));
         const warnMessage: string =
           nullRequiredFields.length === 1
             ? getUiInterface('fieldRequiredSingle', {
                 field: nullRequiredFields[0],
               })
             : getUiInterface('fieldRequiredMultiple', {
                 fields: `${nullRequiredFields.slice(0, -1).join(', ')}${
                   nullRequiredFields.length > 1
                     ? ` ${getWordConnector('and')} `
                     : ''
                 }${nullRequiredFields.slice(-1)}`,
               });
   
         setAlert({ message: warnMessage, variant: 'danger' });
       } else {
         setInvalidFields(new Set()); 
         handleNextPage();
       }
     };

  return (
    <Container fluid>
      <NavBar items={navItems} />
      <div className="mb-3"></div>
      {alert && (
        <AlertOverlay
          message={alert.message}
          variant={alert.variant}
          onClose={() => setAlert(null)}
        />
      )}

      <h2>{getPageName('editCargo')}</h2>
      <Form onSubmit={handleSubmit}>
        <Row className="mb-3">
          <Col md={4}>
            <Form.Group controlId="from">
              <Form.Label>{t('cargo.fields.from')}</Form.Label>
              <div style={{ position: 'relative' }}>
                <Form.Control
                  as="select"
                  name="fromId"
                  value={formData?.FromId || ''}
                  onChange={handleChange}
                  isInvalid={invalidFields.has('From')}
                  style={{
                    backgroundImage: 'none',
                  }}
                >
                  <option value="">Select Option</option>
                  {distinctOptions['wareHouse']?.map(
                    (option: { id: number; description: string }) => (
                      <option key={option.id} value={option.id.toString()}>
                        {option.description}
                      </option>
                    )
                  )}
                </Form.Control>
                <FaChevronDown
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                  }}
                />
              </div>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group controlId="client">
              <Form.Label>Client</Form.Label>
              <div style={{ position: 'relative' }}>
                <Form.Control
                  as="select"
                  name="clientId"
                  value={formData?.ClientId || ''}
                  onChange={handleChange}
                  isInvalid={invalidFields.has('Client')}
                  style={{
                    backgroundImage: 'none',
                  }}
                >
                  <option value="">Select Option</option>
                  {distinctOptions['client']?.map(
                    (option: { id: number; description: string }) => (
                      <option key={option.id} value={option.id.toString()}>
                        {option.description}
                      </option>
                    )
                  )}
                </Form.Control>
                <FaChevronDown
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                  }}
                />
              </div>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group controlId="fornecedor">
              <Form.Label>Fornecedor</Form.Label>
              <div style={{ position: 'relative' }}>
                <Form.Control
                  as="select"
                  name="supplierId"
                  value={formData?.SupplierId ?? ''}
                  onChange={handleChange}
                  isInvalid={invalidFields.has('Fornecedor')}
                  style={{
                    backgroundImage: 'none',
                  }}
                >
                  <option value="">Select Option</option>
                  {distinctOptions['supplier']?.map(
                    (option: { id: number; description: string }) => (
                      <option key={option.id} value={option.id.toString()}>
                        {option.description}
                      </option>
                    )
                  )}
                </Form.Control>
                <FaChevronDown
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                  }}
                />
              </div>
            </Form.Group>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={4}>
            <Form.Group controlId="pu">
              <Form.Label>PU</Form.Label>

              <Form.Control
                type="date"
                name="pu"
                value={formData.Pu ? new Date(formData.Pu).toISOString().split('T')[0] : ''}
                onChange={handleChange}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group controlId="dawb">
              <Form.Label>Dawb</Form.Label>

              <Form.Control
                type="text"
                name="dawb"
                value={formData?.Dawb}
                onChange={handleChange}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group controlId="arrival">
              <Form.Label>Arrival</Form.Label>

              <Form.Control
                type="date"
                name="arrivalDate"
                value={
                  formData.ArrivalDate
                    ? new Date(formData.ArrivalDate).toISOString().split('T')[0]
                    : ''
                }
                onChange={handleChange}
              />
            </Form.Group>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={4}>
            <Form.Group controlId="Pieces">
              <Form.Label>{t('cargo.fields.quantityOfPieces')}</Form.Label>

              <Form.Control
                type="number"
                name="Pieces"
                value={formData.Pieces}
                onChange={handleChange}
              />
            </Form.Group>
          </Col>
        </Row>

        <h5>Dimensions of Pieces</h5>
        <Card
          className="p-3 mb-3"
          style={{
            backgroundColor: '#f8f9fa',
            border: '1px solid #ced4da',
            borderRadius: '8px',
          }}
        >
          {formData?.Dimensions.map((dimension, index) => (
            <Row key={index} className="mb-3">
              <Col md={2}>
                <Form.Group controlId={`quantityOfPieces-${index}`}>
                  <Form.Label>Quantity of Pieces</Form.Label>
                  <Form.Control
                    type="number"
                    name="quantity"
                    value={dimension.quantity}
                    onChange={(e) => handleDimensionChange(index, e)}
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group controlId={`length-${index}`}>
                  <Form.Label>Length</Form.Label>
                  <Form.Control
                    type="number"
                    name="length"
                    value={dimension.length}
                    onChange={(e) => handleDimensionChange(index, e)}
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group controlId={`width-${index}`}>
                  <Form.Label>Width</Form.Label>
                  <Form.Control
                    type="number"
                    name="width"
                    value={dimension.width}
                    onChange={(e) => handleDimensionChange(index, e)}
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group controlId={`high-${index}`}>
                  <Form.Label>High</Form.Label>
                  <Form.Control
                    type="number"
                    name="height"
                    value={dimension.height}
                    onChange={(e) => handleDimensionChange(index, e)}
                  />
                </Form.Group>
              </Col>
              <Col md={2} className="d-flex align-items-end">
                <Button
                  variant="danger"
                  onClick={() => removeDimension(index)}
                  disabled={formData.Dimensions.length === 1} // Desabilita o botão caso só tenha uma dimensão
                >
                  Remove
                </Button>
              </Col>
            </Row>
          ))}
          <Row className="mt-3">
            <Col>
              <Button variant="primary" onClick={addDimension}>
                Add Dimension
              </Button>
            </Col>
          </Row>
        </Card>

        <Row className="mb-3">
          <Col md={4}>
            <Form.Group controlId="dryIce">
              <Form.Check
                type="checkbox"
                label="Dry Ice"
                name="dgr"
                checked={formData.Dgr}
                onChange={handleChange}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group controlId="dangerousGoods">
              <Form.Check
                type="checkbox"
                label="Dangerous Goods"
                name="dgrGoods"
                checked={formData.DgrGoods}
                onChange={handleChange}
              />
            </Form.Group>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={4}>
            <Form.Group controlId="wgt">
                <Form.Label>WGT</Form.Label>
                <Form.Control
                  type="number"
                  name="weight"
                  value={formData.Weight}
                  onChange={handleChange}
                  isInvalid={invalidFields.has('WGT')}
                  style={{
                    backgroundImage: 'none'
                  }}
                />
              <Form.Text
                className="text-muted"
                style={{ fontStyle: 'italic', fontSize: '0.85rem' }}
              >
                Exemplo: 10.089,001
              </Form.Text>
            </Form.Group>
          </Col>

          <Col md={4}>
            <Form.Group controlId="poNumber">
              <Form.Label>PO RO</Form.Label>

              <Form.Control
                type="text"
                name="poNumber"
                value={formData.PoNumber}
                onChange={handleChange}
                isInvalid={invalidFields.has('poNumber')}
                style={{
                  backgroundImage: 'none'
                }}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group controlId="soNumber">
              <Form.Label>SO#</Form.Label>

              <Form.Control
                type="text"
                name="soNumber"
                value={formData.SoNumber}
                onChange={handleChange}
              />
            </Form.Group>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={4}>
            <Form.Group controlId="wrNumber">
              <Form.Label>{t('cargo.fields.wrNumber')}</Form.Label>

              <Form.Control
                type="text"
                name="wrNumber"
                value={formData.WrNumber}
                onChange={handleChange}
                isInvalid={invalidFields.has('wrNumber')}
                style={{
                  backgroundImage: 'none'
                }}
              />
            </Form.Group>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={12}>
            <Form.Group controlId="description">
              <Form.Label>Remarks</Form.Label>

              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.Description}
                onChange={handleChange}
              />
            </Form.Group>
          </Col>
        </Row>

        <Button
          className="mb-3"
          variant="primary"
          type="submit"
          onClick={handleSubmit}
        >
          {getUiInterface('Next')}
        </Button>
      </Form>
    </Container>
  );
};

export default CargoEditView;
